/**
 * SEO component that queries for data with
 *  Gatsby's useStaticQuery React hook
 *
 * See: https://www.gatsbyjs.org/docs/use-static-query/
 *
 * The props below `locationUrl` are optional
 */

import React from "react"
import PropTypes from "prop-types"
import { Helmet } from "react-helmet"
import { useStaticQuery, graphql } from "gatsby"
import { twitterHandle, homePageThumbnail } from "../../site-constants"
import { absoluteUrl, breadcrumbJsonLd, buildAlternateLinks, localizePath, toOgLocale } from "./seo-utils"
import { languages } from "../../i18n"

function SEO({
  description,
  lang,
  meta,
  title,
  image,
  titleTemplate,
  locationUrl = "",
  canonicalPath,
  availableLanguages = languages,
  siteName,
  imageAlt,
  imageWidth,
  imageHeight,
  twitterCard,
  jsonLd,
  breadcrumbs,
  noindex = false,
  type = `website`,
}) {
  const { site } = useStaticQuery(
    graphql`
      query {
        site {
          siteMetadata {
            title
            description
            image
            siteUrl
          }
        }
      }
    `
  )

  const template = titleTemplate || `%s - ${site.siteMetadata.title}`
  const fullTitle = template.replace("%s", title)
  // no metaDescription if description is null, default metaDescription if
  // description is undefined
  const metaDescription = (description === null) 
    ? undefined
    : description ?? site.siteMetadata.description
  const metaImage = image || site.siteMetadata.image
  const useDefaultImage = !image
  const metaImageWidth = useDefaultImage ? homePageThumbnail.width : imageWidth
  const metaImageHeight = useDefaultImage ?homePageThumbnail.height : imageHeight
  const metaImageAlt = useDefaultImage ? homePageThumbnail.alt : imageAlt
  const metaTwitterCard = useDefaultImage ? "summary_large_image" : twitterCard

  // Opt-in: canonical URL, og:url, og:locale and hreflang alternates for the
  // current language, built from the unlocalized `canonicalPath` (e.g. "/").
  const canonicalUrl = canonicalPath ? absoluteUrl(site.siteMetadata.siteUrl, localizePath(canonicalPath, lang)) : null
  const url = canonicalUrl || locationUrl
  const isLocaleCode = languages.includes(lang)

  const metaTags = [
    { name: `robots`, content: noindex ? `noindex` : undefined },
    { name: `description`, content: metaDescription },
    { property: `og:title`, content: fullTitle },
    { property: `og:description`, content: metaDescription },
    { property: `og:type`, content: type },
    { property: `og:site_name`, content: siteName },
    { property: `og:image`, content: metaImage },
    { property: `og:image:width`, content: metaImageWidth },
    { property: `og:image:height`, content: metaImageHeight },
    { property: `og:image:alt`, content: metaImageAlt },
    { property: `og:url`, content: url },
    ...(canonicalPath && isLocaleCode
      ? [
          { property: `og:locale`, content: toOgLocale(lang) },
          ...availableLanguages.filter((l) => l !== lang).map((l) => ({ property: `og:locale:alternate`, content: toOgLocale(l) })),
        ]
      : []),
    { name: `twitter:card`, content: metaTwitterCard },
    { name: `twitter:site`, content: canonicalPath ? twitterHandle : undefined },
    { name: `twitter:creator`, content: twitterHandle },
    { name: `twitter:title`, content: fullTitle },
    { name: `twitter:description`, content: metaDescription },
    { name: `twitter:image:alt`, content: metaImageAlt },
  ]
    // Don't emit tags without a value (e.g. og:url on pages that don't pass one)
    .filter(({ content }) => content !== undefined && content !== null && content !== "")
    .concat(meta)

  const links = canonicalUrl ? [{ rel: `canonical`, href: canonicalUrl }, ...buildAlternateLinks(site.siteMetadata.siteUrl, canonicalPath, availableLanguages)] : []

  const jsonLdItems = [
    ...(jsonLd ? [].concat(jsonLd) : []),
    ...(breadcrumbs?.length ? [breadcrumbJsonLd(site.siteMetadata.siteUrl, breadcrumbs, lang)] : []),
  ]
  const scripts = jsonLdItems.length
    ? [{ type: `application/ld+json`, innerHTML: JSON.stringify(jsonLdItems.length === 1 ? jsonLdItems[0] : jsonLdItems) }]
    : []

  return (
    <Helmet
      htmlAttributes={{
        lang,
      }}
      title={title}
      titleTemplate={template}
      meta={metaTags}
      link={links}
      script={scripts}
    />
  )
}

SEO.defaultProps = {
  lang: `en`,
  meta: [],
  description: ``,
  twitterCard: `summary`,
}

SEO.propTypes = {
  // Pass null to omit description, og:description, and twitter:description.
  // Pass undefined to fall back to site default description
  description: PropTypes.string,
  lang: PropTypes.string,
  meta: PropTypes.arrayOf(PropTypes.object),
  title: PropTypes.string.isRequired,
  // Defaults to same link preview image as the home page
  image: PropTypes.string,
  titleTemplate: PropTypes.string,
  locationUrl: PropTypes.string,
  // Unlocalized path of the page, e.g. "/" or "/about". Enables canonical,
  // og:url, og:locale, twitter:site and hreflang alternates. `lang` must be a
  // language code from src/i18n.js (e.g. "en-US").
  canonicalPath: PropTypes.string,
  // Languages this page exists in, for hreflang/og:locale:alternate. Defaults
  // to every built language; pass fewer when some translations don't exist.
  availableLanguages: PropTypes.arrayOf(PropTypes.string),
  siteName: PropTypes.string,
  imageAlt: PropTypes.string,
  imageWidth: PropTypes.number,
  imageHeight: PropTypes.number,
  twitterCard: PropTypes.oneOf([`summary`, `summary_large_image`]),
  // A JSON-LD object, or an array of them
  jsonLd: PropTypes.oneOfType([PropTypes.object, PropTypes.array]),
  // Breadcrumb trail from the top of the hierarchy down to this page, represented
  // as { name, path } objects. Paths should be unlocalized.
  breadcrumbs: PropTypes.arrayOf(PropTypes.shape({ name: PropTypes.string.isRequired, path: PropTypes.string.isRequired })),
  // If true, emits <meta name="robots" content="noindex">
  noindex: PropTypes.bool,
  // og:type, e.g. "website" (default) or "article"
  type: PropTypes.string,
}

export default SEO
